import { useState } from 'react';
import {
  Chip, Alert, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Link, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, TextField, Box, IconButton, Tooltip,
} from '@mui/material';
import { Visibility, Download, CheckCircle, Cancel, Edit, PictureAsPdf, Delete, Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { extractApiError } from '../../api/error';
import { epcRfqApi, type RfqPurchaseOrder, type RfqPurchaseOrderDetail, type RfqPoItem } from '../../api/epcRfq.api';

const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const statusColor = (s: string) => (s === 'approved' ? 'success' : s === 'rejected' ? 'error' : 'default');

/**
 * Purchase Orders auto-generated from RFQs. When an RFQ is closed, each material is awarded to its
 * lowest-rate vendor and one PO per vendor is created. From View you can approve (capturing a delivery
 * address), modify, reject, and — once approved — download the professional PO PDF.
 */
export function EpcRfqPurchaseOrdersPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [openId, setOpenId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['epc-rfq-po', page, limit], queryFn: () => epcRfqApi.listPurchaseOrders({ page, limit }) });

  const columns: Column<RfqPurchaseOrder>[] = [
    { key: 'poCode', label: 'PO #' },
    { key: 'rfqCode', label: 'RFQ #' },
    { key: 'vendorName', label: 'Vendor', render: (r) => r.vendorName ?? '—' },
    { key: 'itemCount', label: 'Materials', align: 'right', render: (r) => r.itemCount },
    { key: 'totalAmount', label: 'PO Value', align: 'right', render: (r) => inr(r.totalAmount) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} /> },
    { key: 'createdAt', label: 'Created', render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—') },
  ];

  return (
    <>
      <PageHeader title="Purchase Orders" subtitle="Vendor-wise POs auto-generated when an RFQ is closed. Approve (with delivery address), modify or reject, then download the PO document." />
      {!isLoading && (data?.data.length ?? 0) === 0 && <Alert severity="info" sx={{ mb: 2 }}>No purchase orders yet. Close an RFQ (with quotations) to auto-generate vendor-wise POs.</Alert>}
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading} rowKey={(r) => r.id}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }}
        actions={(r) => <Button size="small" startIcon={<Visibility />} onClick={() => setOpenId(r.id)}>View</Button>}
        emptyTitle="No purchase orders" emptyDescription="Close an RFQ to auto-generate vendor-wise POs."
      />
      {openId != null && <PoDetailDialog id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function PoDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'approve' | 'modify'>('view');
  const { data: po, isLoading } = useQuery({ queryKey: ['epc-rfq-po', 'detail', id], queryFn: () => epcRfqApi.getPurchaseOrder(id) });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['epc-rfq-po'] }); };
  const reject = useMutation({
    mutationFn: (remarks: string | null) => epcRfqApi.rejectPurchaseOrder(id, remarks),
    onSuccess: invalidate, onError: (e) => setErr(extractApiError(e)),
  });
  const downloadPdf = useMutation({
    mutationFn: () => epcRfqApi.downloadPurchaseOrderPdf(id, po!.poCode),
    onError: (e) => setErr(extractApiError(e)),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {po ? po.poCode : 'Purchase Order'}
        {po && <Chip size="small" sx={{ ml: 1 }} color={statusColor(po.approvalStatus)} label={po.approvalStatus} />}
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {isLoading && <Typography>Loading…</Typography>}
        {po && mode === 'view' && <PoView po={po} />}
        {po && mode === 'approve' && <ApproveForm po={po} onDone={() => { setMode('view'); invalidate(); }} onCancel={() => setMode('view')} />}
        {po && mode === 'modify' && <ModifyForm po={po} onDone={() => { setMode('view'); invalidate(); }} onCancel={() => setMode('view')} />}
      </DialogContent>
      {po && mode === 'view' && (
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {po.approvalStatus === 'pending' && <Button startIcon={<CheckCircle />} color="success" variant="contained" onClick={() => setMode('approve')}>Approve</Button>}
          {po.approvalStatus !== 'approved' && <Button startIcon={<Edit />} onClick={() => setMode('modify')}>Modify</Button>}
          {po.approvalStatus === 'pending' && <Button startIcon={<Cancel />} color="error" onClick={() => { const r = window.prompt('Reason for rejection (optional):') ?? null; reject.mutate(r); }}>Reject</Button>}
          {po.approvalStatus === 'approved' && <Button startIcon={<PictureAsPdf />} variant="contained" onClick={() => downloadPdf.mutate()} disabled={downloadPdf.isPending}>Download PO (PDF)</Button>}
          <Box flex={1} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function PoView({ po }: { po: RfqPurchaseOrderDetail }) {
  return (
    <Stack spacing={2}>
      {po.approvalStatus === 'rejected' && po.approvalRemarks && <Alert severity="error">Rejected: {po.approvalRemarks}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" useFlexGap>
        <Typography variant="body2"><b>RFQ:</b> {po.rfqCode}</Typography>
        <Typography variant="body2"><b>PO Date:</b> {po.poDate ? new Date(po.poDate).toLocaleDateString('en-IN') : '—'}</Typography>
        <Typography variant="body2"><b>Quotation Ref:</b> {po.quotationNumber ?? '—'}{po.quotationDate ? ` (${new Date(po.quotationDate).toLocaleDateString('en-IN')})` : ''}</Typography>
        {po.quotationUrl && <Typography variant="body2"><b>Quotation:</b> <Link href={po.quotationUrl} target="_blank" rel="noreferrer"><Download fontSize="inherit" /> Download</Link></Typography>}
      </Stack>
      <Box>
        <Typography variant="subtitle2">Vendor</Typography>
        <Typography variant="body2">{po.vendorName ?? '—'}{po.vendorAddress ? ` · ${po.vendorAddress}` : ''}</Typography>
        <Typography variant="caption" color="text.secondary">
          {[po.vendorGst && `GST: ${po.vendorGst}`, po.vendorPan && `PAN: ${po.vendorPan}`, po.vendorContact].filter(Boolean).join('  ·  ') || '—'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="subtitle2">Delivery</Typography>
        <Typography variant="body2">{po.deliveryAddress ?? <i>Not set — add on approval</i>}</Typography>
        <Typography variant="caption" color="text.secondary">Payment: {po.paymentTerms ?? '—'} · Delivery: {po.deliveryTerms ?? '—'}</Typography>
      </Box>
      <ItemsTable items={po.items} total={po.totalAmount} />
    </Stack>
  );
}

function ItemsTable({ items, total }: { items: RfqPoItem[]; total: number }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>{it.description}</TableCell>
              <TableCell align="right">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</TableCell>
              <TableCell align="right">{inr(it.rate)}</TableCell>
              <TableCell align="right">{inr(it.amount)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ApproveForm({ po, onDone, onCancel }: { po: RfqPurchaseOrderDetail; onDone: () => void; onCancel: () => void }) {
  const [address, setAddress] = useState(po.deliveryAddress ?? '');
  const [deliveryTerms, setDeliveryTerms] = useState(po.deliveryTerms ?? '');
  const [err, setErr] = useState<string | null>(null);
  const { data: addresses } = useQuery({ queryKey: ['epc-delivery-addresses'], queryFn: () => epcRfqApi.listDeliveryAddresses() });

  const approve = useMutation({
    mutationFn: () => epcRfqApi.approvePurchaseOrder(po.id, { deliveryAddress: address.trim(), deliveryTerms: deliveryTerms.trim() || null }),
    onSuccess: onDone, onError: (e) => setErr(extractApiError(e)),
  });

  return (
    <Stack spacing={2}>
      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      <Typography variant="subtitle2">Approve — Delivery details</Typography>
      <Autocomplete
        freeSolo
        options={(addresses ?? []).map((a) => a.address)}
        value={address}
        onInputChange={(_, v) => setAddress(v)}
        renderInput={(params) => <TextField {...params} label="Delivery address / site" required multiline minRows={2}
          helperText="Pick a previously used address or type a new one — new ones are saved for next time." />}
      />
      <TextField label="Delivery terms (this PO)" value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)} fullWidth
        placeholder="e.g. Within 7 days, freight included, at site" helperText="Selected fresh for every PO." />
      <Stack direction="row" spacing={2}>
        <Button variant="contained" color="success" disabled={!address.trim() || approve.isPending} onClick={() => approve.mutate()}>Approve PO</Button>
        <Button onClick={onCancel}>Back</Button>
      </Stack>
    </Stack>
  );
}

function ModifyForm({ po, onDone, onCancel }: { po: RfqPurchaseOrderDetail; onDone: () => void; onCancel: () => void }) {
  const [rows, setRows] = useState(po.items.map((it) => ({ description: it.description, unit: it.unit ?? '', quantity: String(it.quantity), rate: String(it.rate) })));
  const [deliveryTerms, setDeliveryTerms] = useState(po.deliveryTerms ?? '');
  const [err, setErr] = useState<string | null>(null);

  const setRow = (i: number, k: keyof (typeof rows)[number], v: string) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => [...p, { description: '', unit: '', quantity: '0', rate: '0' }]);
  const delRow = (i: number) => setRows((p) => p.filter((_, idx) => idx !== i));

  const save = useMutation({
    mutationFn: () => epcRfqApi.modifyPurchaseOrder(po.id, {
      items: rows.filter((r) => r.description.trim()).map((r) => ({ description: r.description.trim(), unit: r.unit.trim() || null, quantity: Number(r.quantity) || 0, rate: Number(r.rate) || 0 })),
      deliveryTerms: deliveryTerms.trim() || null,
    }),
    onSuccess: onDone, onError: (e) => setErr(extractApiError(e)),
  });

  return (
    <Stack spacing={2}>
      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      <Typography variant="subtitle2">Modify line items</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 80 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100 }}>Qty</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120 }}>Rate</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell><TextField size="small" fullWidth value={r.description} onChange={(e) => setRow(i, 'description', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" value={r.unit} onChange={(e) => setRow(i, 'unit', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.quantity} onChange={(e) => setRow(i, 'quantity', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.rate} onChange={(e) => setRow(i, 'rate', e.target.value)} /></TableCell>
                <TableCell><Tooltip title="Remove"><IconButton size="small" onClick={() => delRow(i)}><Delete fontSize="inherit" /></IconButton></Tooltip></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button startIcon={<Add />} size="small" onClick={addRow} sx={{ alignSelf: 'flex-start' }}>Add line</Button>
      <TextField label="Delivery terms" value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)} fullWidth />
      <Stack direction="row" spacing={2}>
        <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>Save changes</Button>
        <Button onClick={onCancel}>Back</Button>
      </Stack>
    </Stack>
  );
}
